import React from 'react'
import { Element } from 'react-bulma-components'
import ReactTooltip from 'react-tooltip'
import { CellValue } from './types'

/**
 * Props du composant GenericCell
 */
interface GenericCellProps {
  /** Valeur structurée à afficher (priorité sur title/children) */
  cellValue?: CellValue
  /** Titre simple (pour les headers) */
  title?: string
  /** Contenu du tooltip (optionnel) */
  tooltipContent?: React.ReactNode
  /** Afficher la valeur décimale (par défaut true si disponible) */
  showDecimal?: boolean
  /** Contenu custom en ReactNode (fallback si cellValue/title non fourni) */
  children?: React.ReactNode
  /** ID unique pour le tooltip (optionnel, généré automatiquement si non fourni) */
  tooltipId?: string
}

/**
 * Composant générique pour les cellules et headers du tableau des semaines
 *
 * Gère l'affichage standardisé avec :
 * - Valeur décimale (heures décimales) en gras
 * - Valeur principale (heures:minutes) en petit en dessous
 * - État "N/A" en gris
 * - Tooltip optionnel
 * - Support pour headers simples et custom
 *
 * @param props - Props du composant
 * @returns Cellule ou header avec affichage standardisé
 */
const GenericCell: React.FC<GenericCellProps> = ({
  cellValue,
  title,
  tooltipContent,
  showDecimal = true,
  children,
  tooltipId,
}) => {
  const id = tooltipId || `tooltip-${Math.random().toString(36).substr(2, 9)}`

  // Déterminer le contenu à afficher
  let content: React.ReactNode

  if (cellValue) {
    const { value, decimal, isNA, color } = cellValue

    if (isNA) {
      content = <Element textColor={color || 'grey'}>{value}</Element>
    } else {
      content = (
        <Element>
          {/* Afficher d'abord les heures décimales en gras */}
          {showDecimal && decimal && (
            <>
              <Element renderAs="strong" textColor={color || 'black'}>
                {decimal}
              </Element>
              <br />
            </>
          )}
          {/* Puis afficher les heures en minutes en petit en dessous */}
          <Element renderAs="small">{value}</Element>
        </Element>
      )
    }
  } else {
    content = children || title
  }

  // Si pas de tooltip, retourner directement le contenu
  if (!tooltipContent) {
    return <Element>{content}</Element>
  }

  // Avec tooltip
  return (
    <>
      <Element data-tip data-for={id}>
        {content}
      </Element>
      <ReactTooltip id={id} effect="solid" place="bottom">
        {tooltipContent}
      </ReactTooltip>
    </>
  )
}

export default GenericCell
